<script setup>
import { computed } from 'vue';

const props = defineProps({
  type: {
    type: String,
    required: true,
    validator: (value) => ['instagram', 'whatsapp'].includes(value),
  },
  previewData: {
    type: Object,
    required: true,
  },
});

// Instagram computed properties
const instagramData = computed(() => {
  if (props.type !== 'instagram') return null;
  return props.previewData;
});

const thumbnailUrl = computed(() => {
  if (props.type === 'instagram') {
    return instagramData.value?.thumbnail_url || instagramData.value?.media_url;
  }
  return props.previewData?.thumbnailUrl;
});

const mediaType = computed(() => {
  if (props.type === 'instagram') {
    return instagramData.value?.media_type || 'IMAGE';
  }
  return props.previewData?.media_type || 'IMAGE';
});

const title = computed(() => {
  if (props.type === 'instagram') {
    // Instagram doesn't have title, but we can use caption if short
    const caption = instagramData.value?.caption;
    if (caption && caption.length < 60) return caption;
    return null;
  }
  return props.previewData?.title;
});

const description = computed(() => {
  if (props.type === 'instagram') {
    return instagramData.value?.caption || '';
  }
  return props.previewData?.body || '';
});

const linkUrl = computed(() => {
  if (props.type === 'instagram') {
    return instagramData.value?.permalink;
  }
  return props.previewData?.sourceUrl;
});

const linkText = computed(() => {
  if (props.type === 'instagram') {
    return 'Ver no Instagram';
  }
  return 'Ver origem';
});

const sourceApp = computed(() => {
  if (props.type === 'whatsapp') {
    return props.previewData?.sourceApp || props.previewData?.entryPointConversionApp;
  }
  return null;
});

const mediaTypeIcon = computed(() => {
  const type = mediaType.value?.toUpperCase();
  if (type === 'VIDEO' || type === 'REELS') return 'i-lucide-play-circle';
  if (type === 'CAROUSEL_ALBUM') return 'i-lucide-images';
  return 'i-lucide-image';
});

const mediaTypeLabel = computed(() => {
  const type = mediaType.value?.toUpperCase();
  if (type === 'VIDEO') return 'Video';
  if (type === 'REELS') return 'Reels';
  if (type === 'CAROUSEL_ALBUM') return 'Album';
  return 'Imagem';
});
</script>

<template>
  <div
    class="flex flex-col gap-2 w-full p-3 mb-2 rounded-lg border border-n-slate-3 dark:border-n-slate-7 bg-n-slate-2 dark:bg-n-slate-1 overflow-hidden"
  >
    <!-- Thumbnail Image -->
    <a
      v-if="thumbnailUrl"
      :href="linkUrl"
      target="_blank"
      rel="noopener noreferrer"
      class="block relative rounded-md overflow-hidden hover:opacity-90 transition-opacity"
    >
      <img
        :src="thumbnailUrl"
        :alt="title || 'Media preview'"
        class="w-full h-auto object-cover max-h-64"
      />
      <!-- Media Type Badge -->
      <div
        class="absolute top-2 right-2 flex items-center gap-1 px-2 py-1 rounded bg-n-alpha-black1 dark:bg-n-alpha-black2 backdrop-blur-sm"
      >
        <span :class="[mediaTypeIcon, 'text-white text-sm']" />
        <span class="text-white text-xs font-medium">{{ mediaTypeLabel }}</span>
      </div>
    </a>

    <!-- Content -->
    <div class="flex flex-col gap-1">
      <!-- Title/Headline -->
      <h4
        v-if="title"
        class="text-sm font-medium text-n-slate-12 dark:text-n-slate-12 m-0 line-clamp-1"
      >
        {{ title }}
      </h4>

      <!-- Description/Caption -->
      <p
        v-if="description"
        class="text-xs text-n-slate-11 dark:text-n-slate-11 m-0 line-clamp-2"
      >
        {{ description }}
      </p>

      <!-- Footer with Source App and Link -->
      <div class="flex items-center justify-between gap-2 mt-1">
        <!-- Source App Badge (WhatsApp only) -->
        <div
          v-if="sourceApp"
          class="flex items-center gap-1 px-2 py-0.5 rounded-full bg-n-slate-3 dark:bg-n-slate-8"
        >
          <span class="i-lucide-circle-dot text-n-slate-11 text-xs" />
          <span class="text-xs text-n-slate-11 dark:text-n-slate-11">{{
            sourceApp
          }}</span>
        </div>
        <div v-else />

        <!-- View Link -->
        <a
          v-if="linkUrl"
          :href="linkUrl"
          target="_blank"
          rel="noopener noreferrer"
          class="flex items-center gap-1 text-xs text-n-iris-9 dark:text-n-iris-9 hover:text-n-iris-10 dark:hover:text-n-iris-10 font-medium"
        >
          <span>{{ linkText }}</span>
          <span class="i-lucide-external-link text-xs" />
        </a>
      </div>
    </div>
  </div>
</template>
